import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { ColunaPlanilhaRateio, Distribuidora } from '../../../types';

const COLUNAS_DISPONIVEIS: { value: ColunaPlanilhaRateio; label: string }[] = [
  { value: 'uc', label: 'UC' },
  { value: 'outro_numero_uc', label: 'Outro Nº UC' },
  { value: 'titular', label: 'Titular' },
  { value: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { value: 'percent', label: '% do Excedente' },
  { value: 'energia_alvo_kwh', label: 'Energia-Alvo (kWh)' },
];
const COLUNAS_DEFAULT: ColunaPlanilhaRateio[] = ['uc', 'titular', 'cpf_cnpj', 'percent'];

interface DistribuidoraFormModalProps {
  distribuidora?: Distribuidora | null;
  onClose: () => void;
  onSaved: (distribuidora: Distribuidora) => void;
}

const DistribuidoraFormModal: React.FC<DistribuidoraFormModalProps> = ({ distribuidora, onClose, onSaved }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [nome, setNome] = useState(distribuidora?.nome || '');
  const [emailProtocolo, setEmailProtocolo] = useState(distribuidora?.email_protocolo || '');
  const [diaLeituraPadrao, setDiaLeituraPadrao] = useState(
    distribuidora?.dia_leitura_padrao != null ? String(distribuidora.dia_leitura_padrao) : ''
  );
  const [webhookProtocoloUrl, setWebhookProtocoloUrl] = useState(distribuidora?.webhook_protocolo_url || '');
  const [colunasPlanilha, setColunasPlanilha] = useState<ColunaPlanilhaRateio[]>(
    distribuidora?.colunas_planilha && distribuidora.colunas_planilha.length > 0 ? distribuidora.colunas_planilha : COLUNAS_DEFAULT
  );
  const [ativo, setAtivo] = useState(distribuidora?.ativo ?? true);

  const toggleColuna = (col: ColunaPlanilhaRateio) => {
    setColunasPlanilha(prev => (prev.includes(col) ? prev.filter(c => c !== col) : [...prev, col]));
  };
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!nome.trim()) {
      addToast('Informe o nome da distribuidora.', 'error');
      return;
    }
    const dia = diaLeituraPadrao.trim() ? parseInt(diaLeituraPadrao, 10) : null;
    if (dia != null && (dia < 1 || dia > 31)) {
      addToast('Dia de leitura padrão deve ser entre 1 e 31.', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nome: nome.trim(),
        email_protocolo: emailProtocolo.trim() || null,
        dia_leitura_padrao: dia,
        webhook_protocolo_url: webhookProtocoloUrl.trim() || null,
        colunas_planilha: colunasPlanilha.length > 0 ? colunasPlanilha : COLUNAS_DEFAULT,
        ativo,
      };

      if (distribuidora) {
        const { data, error } = await supabase
          .from('distribuidora_config')
          .update(payload)
          .eq('id', distribuidora.id)
          .select()
          .single();
        if (error) throw error;
        addToast('Distribuidora atualizada com sucesso.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_distribuidoras', acao: 'atualizar_distribuidora', origem: 'acao' });
        onSaved(data as Distribuidora);
      } else {
        const { data, error } = await supabase
          .from('distribuidora_config')
          .insert({ ...payload, created_by: profile?.id })
          .select()
          .single();
        if (error) throw error;
        addToast('Distribuidora criada com sucesso.', 'success');
        logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_distribuidoras', acao: 'criar_distribuidora', origem: 'acao' });
        onSaved(data as Distribuidora);
      }
    } catch (error) {
      console.error('Error saving distribuidora', error);
      addToast('Erro ao salvar a distribuidora.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins">
            {distribuidora ? 'Editar Distribuidora' : 'Nova Distribuidora'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome *</label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Ex: Equatorial GO"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">E-mail de Protocolo</label>
            <input
              type="email"
              value={emailProtocolo}
              onChange={e => setEmailProtocolo(e.target.value)}
              placeholder="protocolo@distribuidora.com.br"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dia de Leitura Padrão</label>
            <input
              type="number"
              min={1}
              max={31}
              value={diaLeituraPadrao}
              onChange={e => setDiaLeituraPadrao(e.target.value)}
              placeholder="Ex: 15"
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Webhook de Protocolo Próprio (opcional)
            </label>
            <input
              type="url"
              value={webhookProtocoloUrl}
              onChange={e => setWebhookProtocoloUrl(e.target.value)}
              placeholder="https://hook.us1.make.com/..."
              className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Se vazio, o envio de planilha usa o webhook genérico configurado em Config &gt; APIs.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Colunas da Planilha de Protocolo</label>
            <div className="grid grid-cols-2 gap-1.5">
              {COLUNAS_DISPONIVEIS.map(col => (
                <label key={col.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input type="checkbox" checked={colunasPlanilha.includes(col.value)} onChange={() => toggleColuna(col.value)} />
                  {col.label}
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Layout exato por distribuidora ainda não é validado — a ordem das colunas selecionadas segue sempre a ordem padrão.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={ativo} onChange={e => setAtivo(e.target.checked)} />
            Ativa
          </label>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DistribuidoraFormModal;
